import type {
  BaseHTMLAttributes,
  ChangeEvent,
  ComponentProps,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react'
import { useCallback, useEffect, useState } from 'react'

import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import type {
  FieldValues,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { usePrevious } from '@twistopayments/design-system/hooks'
import { ChevronDownIcon } from '@twistopayments/design-system/icons/inline'
import { hasString, localeIncludes } from '@twistopayments/design-system/utils'

import { IconButton } from '../../atoms/button'
import type { InputProps } from '../../atoms/input'

import type { DropDownItemText } from './dropdown'

const focusDebounceDelay = 10

export type Option<T> = ComponentProps<typeof DropDownItemText> & {
  value: T
  id: string
}

type Props<T> = {
  options: Array<Option<T>>
  name: string
  setFormValue?: UseFormSetValue<FieldValues> | undefined
  onOptionSelect?: (selectedSuggestion: Option<T>) => void
  onInputChange?: (value: string) => void
  getInputValueOnSelect?: (selectedOption: Option<T>) => string
  defaultOption?: Option<T>
  initialInputValue?: string
  persistSelection?: boolean
  filterLocally?: boolean
}

export const useAutocomplete = <T extends unknown>({
  options,
  name,
  onOptionSelect,
  onInputChange,
  getInputValueOnSelect,
  defaultOption,
  initialInputValue = '',
  persistSelection = true,
  filterLocally = true,
}: Props<T>) => {
  const { setValue: setFormValue, clearErrors } =
    (useFormContext() as UseFormReturn | null) || {}

  // state which allow user to navigate throw dropdown list by keyboard
  const [focusedSuggestion, setFocusedSuggestion] = useState<number | null>(
    null
  )

  // the value of visible search input
  const [searchInputValue, setSearchInputValue] = useState(
    defaultOption?.primary.toString() ?? initialInputValue
  )
  const prevSearchInputValue = usePrevious(searchInputValue)

  // control state of focus
  const [focused, setFocused] = useState(false)
  const [debouncedFocused] = useDebounce(focused, focusDebounceDelay)
  const prevFocused = usePrevious(focused)

  // chosenSuggestion is the value selected from the list of options
  const [selectedOption, setSelectedOption] = useState<Option<T> | null>(
    defaultOption ?? null
  )
  const prevSelectedSuggestion = usePrevious(selectedOption)

  const isUserTyping =
    searchInputValue !== (selectedOption?.primary.toString() ?? '')
  const filteredOptions =
    filterLocally && isUserTyping
      ? options.filter((option) =>
          localeIncludes(option.primary.toString(), searchInputValue)
        )
      : options

  // function to clear form
  const resetInputValue = useCallback(() => {
    setSearchInputValue('')
    setSelectedOption(null)

    if (persistSelection) {
      setFormValue && setFormValue(name, '')
    }
  }, [name, persistSelection, setFormValue])

  // whenever chosen suggestion is changed we need to update value of input
  useEffect(() => {
    if (selectedOption) {
      setSearchInputValue(
        getInputValueOnSelect?.(selectedOption) ??
          selectedOption.primary.toString()
      )
    }

    if (persistSelection) {
      setFormValue && setFormValue(name, selectedOption?.id)
    }
  }, [
    selectedOption,
    name,
    persistSelection,
    setFormValue,
    getInputValueOnSelect,
  ])

  useEffect(() => {
    const valueChange = searchInputValue !== prevSearchInputValue

    // if value of input has changed but there is no more text in input...
    // ...we want to reset input
    if (valueChange && !hasString(searchInputValue)) {
      resetInputValue()
    }
  }, [searchInputValue, resetInputValue, focused, prevSearchInputValue])

  useEffect(() => {
    // get the display text from chosen suggestion
    const primaryValue = selectedOption?.primary.toString()
    const isBlurred = prevFocused === false && focused === false

    // check if value has changed on blurred input
    // and compare/check if the current displayed value equals to value of chosen suggestion
    // and if not set value to be same as previously selected suggestion
    if (
      persistSelection &&
      isBlurred &&
      hasString(primaryValue) &&
      hasString(prevSelectedSuggestion?.primary) &&
      searchInputValue !== primaryValue
    ) {
      setSearchInputValue(primaryValue)
    }

    // on blur check if we have value but no suggestion set and reset form
    if (
      persistSelection &&
      isBlurred &&
      hasString(searchInputValue) &&
      isNil(selectedOption?.id)
    ) {
      resetInputValue()
    }
  }, [
    selectedOption,
    searchInputValue,
    focused,
    prevFocused,
    prevSelectedSuggestion,
    resetInputValue,
    persistSelection,
  ])

  const handleBlur = () => setFocused(false)
  const handleFocus = ({ target }: FocusEvent<HTMLDivElement>) => {
    if (target.tagName === 'INPUT') {
      setFocusedSuggestion(null)
    }
    setFocused(true)
  }

  // add support for keyboards
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const container = e.currentTarget

    if (
      !debouncedFocused ||
      !options.length ||
      (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')
    ) {
      return
    }

    e.preventDefault()

    if (
      e.key === 'ArrowDown' &&
      (focusedSuggestion === null ||
        focusedSuggestion < filteredOptions.length - 1)
    ) {
      let suggestionToFocus = 0
      if (focusedSuggestion !== null) {
        suggestionToFocus = focusedSuggestion + 1
      }

      setFocusedSuggestion(suggestionToFocus)

      return container.getElementsByTagName('li')[suggestionToFocus].focus()
    }

    if (
      e.key === 'ArrowUp' &&
      focusedSuggestion !== null &&
      focusedSuggestion < options.length
    ) {
      if (focusedSuggestion === 0) {
        setFocusedSuggestion(null)

        return container.getElementsByTagName('input')[0].focus()
      }

      const suggestionToFocus = focusedSuggestion - 1
      setFocusedSuggestion(suggestionToFocus)

      return container.getElementsByTagName('li')[suggestionToFocus].focus()
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value)
    onInputChange?.(event.target.value)
  }
  // when user clicks to input we want to preselect current value, so its easier to manipulate with it
  const handleOnClick = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select()
  }

  const handleOptionSelect = (suggestion: Option<T>) => () => {
    setSelectedOption(suggestion)
    setFocused(false)
    clearErrors?.(name)
    onOptionSelect && onOptionSelect(suggestion)
  }

  const rootProps: BaseHTMLAttributes<HTMLDivElement> = {
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
  }

  const renderedInputProps: InputProps = {
    name: `visible-input-of-${name}`,
    endAdornment: (
      <IconButton
        aria-label={
          /* i18n */ i18n._('components.molecules.autocomplete.ariaLabel')
        }
        color="ghost"
        icon={ChevronDownIcon}
        size="small"
        tabIndex={-1} // turning off the accessibility via 'tab'
      />
    ),
    nativeInputProps: {
      autoComplete: 'new-password', // prevents displaying native autocomplete
    },
    value: searchInputValue,
    onClick: handleOnClick,
    onChange: handleOnChange,
  }

  return {
    rootProps,
    filteredOptions,
    renderedInputProps,
    handleOptionSelect,
    selectedOption,
    openDropdown: debouncedFocused,
  }
}
